import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { deleteButton, editButton, notepadIconWithoutBg } from "./SvgIcons";
import { Center, Col } from "@/common/css";
import EmptyList from "@/components/common/EmptyList";
import { Button } from "@/components/ui/button";
import { useEmployeeInfo } from "@/services/employee.services";
import React from "react";
import { LightText2 } from "@/common/ui/Text";
import { AiTwotoneEdit } from "react-icons/ai";
import { FaRegTrashCan } from "react-icons/fa6";
import { useAppDispatch } from "@/store/store";
import { setNoteUpdate } from "@/store/features/employeeSlice";

interface NoteCardProps {
  user: { name: string; profilePic?: string };
  activity: string;
  dateTime: string;
  noteId: string;
  openModal: (modalKey: string, props?: any) => void;
  refetch: any;
}

import { motion } from "framer-motion";
import Icon from "@/common/ui/Image";
import { useSelector } from "react-redux";
import { pageEventSelector } from "@/store/features/dashboardSlice";
import { convertToLocalDateTime } from "@/utils/helper/moment";

const shimmerVariants = {
  animate: {
    opacity: [0.2, 1, 0.2],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

const NoteCardSkeleton = () => {
  return (
    <div className="relative space-y-2 border-b pb-3">
      <div className="flex items-start gap-2">
        <motion.div className="w-4 h-4 bg-gray-300 rounded" variants={shimmerVariants} animate="animate" />
        <motion.div className="h-4 w-3/4 bg-gray-300 rounded" variants={shimmerVariants} animate="animate" />
      </div>

      <div className="flex items-center justify-between pl-6">
        <div className="flex items-center gap-2">
          <motion.div className="w-6 h-6 rounded-full bg-gray-300" variants={shimmerVariants} animate="animate" />
          <motion.div className="w-24 h-4 bg-gray-300 rounded" variants={shimmerVariants} animate="animate" />
        </div>
        <motion.div className="w-20 h-4 bg-gray-300 rounded" variants={shimmerVariants} animate="animate" />
      </div>
    </div>
  );
};

const NoteActivityCard: React.FC<NoteCardProps> = ({ user, activity, dateTime, noteId, openModal, refetch }) => {
  const dispatch = useAppDispatch();
  return (
    <div className="relative group space-y-2 border-b pb-3">
      <div className="flex items-start gap-2">
        <Icon src="/icons/note-icon.svg" className="w-4 h-4 mt-1 text-text-default" alt="" />
        <div className="text-13 text-text-default overflow-hidden text-ellipsis line-clamp-2" dangerouslySetInnerHTML={{ __html: activity }} />
      </div>
      <div className="flex items-center justify-between pl-6">
        <div className="flex items-center gap-1">
          {user?.profilePic ? (
            <img
              src={user?.profilePic}
              alt={"profile picture"}
              className="h-6 w-6 rounded-full object-cover object-center"
              style={{ minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24 }}
            />
          ) : (
            <div className="w-6 h-6 min-h-6 min-w-6 rounded-full bg-primaryOpacity-5 text-brand-primary text-[10px] font-semibold flex items-center justify-center">
              {user?.name ? user?.name[0] || "" + user?.name.split(" ")?.[1]?.[0] || "" : ""}
            </div>
          )}

          <LightText2 text={user?.name || ""} className="text-xs text-text-default font-medium" />
        </div>
        <LightText2 text={convertToLocalDateTime(dateTime)} className="text-xs text-text-default" />
      </div>
      <div className="absolute -top-2 right-2 group-hover:flex items-center justify-center hidden gap-1">
        <button
          className=" size-8 border-[0.2px] border-borderColor1 bg-[#E6F2FE] flex items-center justify-center rounded hover:border-text-primary"
          onClick={() => dispatch(setNoteUpdate({ noteData: activity, noteId }))}
        >
          <AiTwotoneEdit className="text-text-primary size-4" />
        </button>
        <button
          className="size-8 border-[0.2px] border-borderColor1 bg-[#E6F2FE] flex items-center justify-center rounded hover:border-text-primary"
          onClick={() => openModal("DeleteNote", { data: { noteId }, afterCall: refetch })}
        >
          <FaRegTrashCan className="text-text-error size-4" />
        </button>
      </div>
    </div>
  );
};

export default NoteActivityCard;

const QuickNoteContainer = ({ handleAddEditQuickNote, handleDeleteQuickNote, allDataLoading, openModal, isForEmploymentDetails }: any) => {
  const { notes, fetchNotesValidating } = { notes: { notes: [] }, fetchNotesValidating: "" };
  const location = useLocation();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const pageEvent = useSelector(pageEventSelector);

  const { isLoading, data, refetch } = useEmployeeInfo({
    empId: employeeId,
    view_typ: "activities",
    type: "notes",
  });

  const notesList = notes?.notes
    ?.map((note: any) => note.Data)
    ?.flat()
    .slice(0, 10);

  useEffect(() => {
    if (pageEvent?.keys.includes("notes") && pageEvent?.type === "refresh") {
      refetch();
    }
  }, [pageEvent]);

  if (allDataLoading) {
    return null;
  }

  function updateURLWithSectionAndSubSection(sectionName: any, subSectionName: any) {
    const currentPathName = location.pathname;
    const baseURL = currentPathName.split("23456")[0];
    const newURL = `${baseURL}${23456}/${sectionName}/${subSectionName}`;
    return newURL;
  }
  const isActivities = Object.keys(data?.groupedActivities || {}).length > 0;

  return (
    <div className="quick-note-container">
      <div className={`quick-note-container-details`}>
        <div className="quick-note-add-card" onClick={() => handleAddEditQuickNote(false)}>
          <span>Add a quick note</span>
        </div>
        <div className={` flex flex-col ${isActivities ? "justify-between" : " mt-20"} h-[calc(100%-58px-16px-22px)]`}>
          {Object.keys(data?.groupedActivities || {}).length > 0 && (
            <div className="px-4 overflow-y-auto flex-1">
              {Object.entries(data.groupedActivities).map(([date, activities]: any) => (
                <div key={date} className="space-y-2">
                  {activities.map((activity: any, index: number) => (
                    <NoteActivityCard key={`${date}-${index}`} {...activity} date={date} openModal={openModal} refetch={refetch} />
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className={`px-4 ${isLoading ? "h-full" : ""}`}>
            {isLoading ? (
              <Col className="!gap-5 h-full ">
                <NoteCardSkeleton />
                <NoteCardSkeleton />
                <NoteCardSkeleton />
              </Col>
            ) : (
              <>
                {Object.keys(data?.groupedActivities || {}).length === 0 && (
                  <Center className="h-full">
                    <EmptyList text="You don’t have any notes yet!" icon="/icons/empty-inbox.svg" className="gap-y-4 text-center" />
                  </Center>
                )}
              </>
            )}
          </div>
          {Object.keys(data?.groupedActivities || {}).length > 0 && (
            <Button
              variant="ghost"
              className="text-13 text-text-primary w-full text-base font-semibold bg-primaryOpacity-5 py-6"
              onClick={() => navigate(`${isForEmploymentDetails ? "/home/employment-details/" : "/home/employees/"}${employeeId}/activities?tab=notes`)}
            >
              View all notes
            </Button>
          )}
          {!!notesList?.length && !fetchNotesValidating && (
            <div>
              {notesList &&
                notesList.map((note: any, index: any) => {
                  return (
                    <QuickNoteCard
                      key={index}
                      userName={note?.createdBy?.name}
                      userImage={note?.createdBy?.pf}
                      description={note?.styleNoteData}
                      updatedTime={note?.updatedTs}
                      isLast={index === notesList.length - 1}
                      handleEditNote={() => handleAddEditQuickNote(true, note)}
                      handleDeleteNote={() => handleDeleteQuickNote(note)}
                    />
                  );
                })}
            </div>
          )}
        </div>
      </div>
      {!!notes?.notes?.length && (
        <div>
          <Link to={updateURLWithSectionAndSubSection("activities", "notes")} className="quick-note-container-button">
            View all notes
          </Link>
        </div>
      )}
    </div>
  );
};

const QuickNoteCard = ({
  userImage = "https://d23bil4xe6rci.cloudfront.net/profile_update/1126_02:28:22.jpg",
  userName = "Divya Ramasamy",
  description,
  updatedTime,
  isLast,
  handleEditNote,
  handleDeleteNote,
}: any) => {
  const [displayActionButtons, setDisplayActionButtons] = useState(false);

  return (
    <div
      className={`quick-note-view-card ${!isLast ? "quick-note-view-card-border" : ""}`}
      onMouseEnter={() => setDisplayActionButtons(true)}
      onMouseLeave={() => setDisplayActionButtons(false)}
    >
      <div>{notepadIconWithoutBg}</div>
      <div className="quick-note-view-card-details-container">
        <p className="quick-note-view-card-content">{description}</p>
        <div className="quick-note-view-card-info">
          <div className="d-flex gap-1 align-items-center record-details-user-card">
            {userImage && <img src={userImage} alt="User" className="rounded-circle" />}
            {userName && <span className="record-details-user-card-name">{userName}</span>}
          </div>
          <span className="quick-note-view-card-time">{updatedTime}</span>
        </div>
        {displayActionButtons && (
          <div className="quick-note-edit-delete-buttons-container">
            <button className="quick-note-edit-delete-buttons quick-note-edit-button" onClick={() => handleEditNote?.()}>
              {editButton}
            </button>
            <button className="quick-note-edit-delete-buttons quick-note-delete-button" onClick={() => handleDeleteNote?.()}>
              {deleteButton}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export { QuickNoteContainer, QuickNoteCard };
